/* eslint-disable */
// Paste support for the kitchen "master data" sheet (one sheet per delivery day):
//
//   08-09-2026 | MASTER DATA DETAILS ...                         <- delivery date (first cell)
//   Tuesday    | Recipe I'd | Pax Projection | Total Pax | Standard Portion Size | T.P | Weight ...
//   Breakfast  |            | CT - 1 | Aravali | Atwork | Howe | ATGL | ... same store names again
//   Steamed Idli          0     4   ...        0.18 0.18 ...                     <- item rows
//   Warmaly Yours | ... store names ...                            <- next section header
//
// - A section header row repeats the store names: the 1st block is Pax Projection (the count to
//   order), the 2nd is Standard Portion Size (kg per pax), the 3rd is Weight (ignored).
// - A section whose name matches a shift ("HI - TEA" ~ "Hi-Tea") belongs to that shift; any other
//   name, or a blank one, is Lunch.
// - The portion size picks the variant: the one whose minWeight (grams) equals portion x 1000.
//   No portion size, or one matching no variant -> the product's first variant.
// Only the sections of the shift selected on the screen are taken.
import { createNewRowFromProduct } from '../clipboardUtils';

const norm = (s = '') => String(s ?? '')
	.toLowerCase()
	.replace(/@/g, 'at')
	.replace(/[^a-z0-9]/g, '');

const splitLines = (text) => text.replace(/\r/g, '').split('\n').map((l) => l.split('\t'));

const DATE_RE = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/;

const num = (v) => {
	const s = String(v ?? '').trim();
	if (s === '' || s === '-') return null;
	const n = Number(s);
	return Number.isNaN(n) ? null : n;
};

// True when the clipboard looks like the master sheet: a date in the first cell and a
// "Pax Projection" header somewhere near the top.
export const isMasterSheet = (text) => {
	if (!text) return false;
	const lines = splitLines(text);
	const first = (lines[0]?.[0] ?? '').trim();
	const hasPax = lines.slice(0, 6).some((cells) => cells.some((c) => /pax\s*projection/i.test(c)));
	return DATE_RE.test(first) && hasPax;
};

// A section header repeats the same store names in several blocks. Returns the text cells and
// where each store's 1st (pax) and 2nd (portion) column is, or null for any other row.
const readSectionHeader = (cells) => {
	const seen = {};
	cells.forEach((cell, idx) => {
		if (idx === 0) return;
		const text = (cell ?? '').trim();
		if (!text || num(text) !== null) return;
		(seen[text] = seen[text] || []).push(idx);
	});
	const stores = Object.entries(seen).filter(([, idxs]) => idxs.length >= 2);
	if (stores.length < 2) return null;
	return stores.map(([name, idxs]) => ({ name, paxCol: idxs[0], portionCol: idxs[1] }))
		.sort((a, b) => a.paxCol - b.paxCol);
};

export const parseMasterSheet = (text) => {
	const lines = splitLines(text);
	const dateMatch = (lines[0]?.[0] ?? '').trim().match(DATE_RE);
	const date = dateMatch
		? `${dateMatch[3]}-${dateMatch[2].padStart(2, '0')}-${dateMatch[1].padStart(2, '0')}`
		: null;
	const sections = [];
	let current = null;
	lines.slice(1).forEach((cells) => {
		const header = readSectionHeader(cells);
		if (header) {
			current = { name: (cells[0] ?? '').trim(), stores: header, items: [] };
			sections.push(current);
			return;
		}
		if (!current) return; // title / day / block-heading rows before the first section
		const name = (cells[0] ?? '').trim();
		if (!name || num(name) !== null) return; // total rows and nameless leftovers
		const perStore = {};
		current.stores.forEach((s) => {
			perStore[s.name] = { pax: num(cells[s.paxCol]), portionKg: num(cells[s.portionCol]) };
		});
		current.items.push({ name, perStore });
	});
	return { date, sections };
};

// Sheet section name -> shift ({id, shiftName}); unmatched / blank -> the Lunch shift.
const sectionShift = (sectionName, shifts) => {
	const key = norm(sectionName);
	return (key && shifts.find((s) => norm(s?.shiftName) === key))
		|| shifts.find((s) => norm(s?.shiftName) === 'lunch')
		|| null;
};

// Sheet store name ("CT - 1", "Atwork") -> table header id, by short name or store name.
const matchStoreHeader = (sheetName, tableHeaders) => {
	const key = norm(sheetName);
	if (!key) return null;
	const header = tableHeaders.find((h) => h?.shortName && norm(h.shortName) === key)
		|| tableHeaders.find((h) => h?.storeName && norm(h.storeName) === key)
		|| tableHeaders.find((h) => h?.storeName && norm(h.storeName).includes(key));
	return header?.id ?? null;
};

// The variant an item name + portion (kg) means: same product name, a count product preferred
// (pax are counts), and the variant whose minWeight is the portion in grams.
const resolveVariant = (itemName, portionKg, products) => {
	const key = norm(itemName);
	const byName = products.filter((p) => norm(p?.productName) === key);
	if (!byName.length) return null;
	const counted = byName.filter((p) => p?.unit === 'count');
	const pool = counted.length ? counted : byName;
	if (portionKg !== null && portionKg > 0) {
		const grams = portionKg * 1000;
		const exact = pool.find((p) => p?.minWeight && Math.abs(p.minWeight - grams) < 1e-6);
		if (exact) return exact;
	}
	// No portion size, or one matching no variant's min weight: the first variant.
	return pool[0];
};

/**
 * Builds table rows from a pasted master sheet, in the same shape as
 * clipboardUtils.updateTableRowsWithExcelData.
 * Returns { error } when the paste cannot be applied, else
 * { updatedRows, matchedCount, updatedCount, addedCount, storeMapping, notes }.
 */
export const buildRowsFromMasterSheet = ({
	text, selectedShiftId, selectedDate, shifts = [], tableHeaders = [], stores = [], products = [],
}) => {
	const { date, sections } = parseMasterSheet(text);
	if (!sections.length) {
		return { error: 'No store columns (CT - 1, Aravali, ...) found in the pasted sheet' };
	}
	if (date && selectedDate && date !== selectedDate) {
		const [y, m, d] = date.split('-');
		return {
			error: `This sheet is for ${d}-${m}-${y}. Set Expected Delivery Date to ${d}/${m}/${y}, then paste again.`,
		};
	}
	const selectedShift = shifts.find((s) => String(s?.id ?? s?.value) === String(selectedShiftId));
	if (!selectedShift) {
		return { error: 'Select the shift first - only that shift\'s part of the sheet is pasted' };
	}

	const mine = sections.filter((sec) => {
		const shift = sectionShift(sec.name, shifts);
		return shift && String(shift.id ?? shift.value) === String(selectedShift.id ?? selectedShift.value);
	});
	if (!mine.length) {
		const found = [...new Set(sections.map((sec) => sectionShift(sec.name, shifts)?.shiftName ?? (sec.name || 'Lunch')))];
		return {
			error: `The sheet has nothing for ${selectedShift.shiftName}. It has: ${found.join(', ')}.`,
		};
	}

	const storeMapping = {};
	const unmappedStores = new Set();
	mine.forEach((sec) => sec.stores.forEach((s) => {
		const id = matchStoreHeader(s.name, tableHeaders);
		if (id !== null) storeMapping[s.name] = id;
		else unmappedStores.add(s.name);
	}));

	// variantId -> row; one row per variant, quantities summed across sections.
	const rowsByVariant = new Map();
	const notFound = new Map(); // item name -> placeholder row
	const ignoredDecimals = [];
	let updatedCount = 0;

	mine.forEach((sec) => sec.items.forEach((item) => {
		sec.stores.forEach((s) => {
			const storeId = storeMapping[s.name];
			const { pax, portionKg } = item.perStore[s.name] || {};
			if (storeId === undefined || pax === null || !(pax > 0)) return;
			if (!Number.isInteger(pax)) {
				ignoredDecimals.push(`${item.name} (${s.name}: ${pax})`);
				return;
			}
			const product = resolveVariant(item.name, portionKg, products);
			if (!product) {
				if (!notFound.has(item.name)) {
					notFound.set(item.name, {
						'Item Name': item.name,
						itemId: null,
						SkuCode: '',
						Total: '',
						_isNotFound: true,
						_excelName: item.name,
						_matchType: 'unidentified',
						...stores.reduce((acc, store) => {
							acc[`${store.id}`] = { qty: 0, unit: '', storeId: store.id, itemId: null, productId: null };
							return acc;
						}, {}),
					});
				}
				return;
			}
			if (!rowsByVariant.has(product.value)) {
				const row = createNewRowFromProduct(product, stores, '');
				row._matchType = 'nameMatching';
				row._isNewFromDropdown = true;
				rowsByVariant.set(product.value, row);
			}
			const row = rowsByVariant.get(product.value);
			if (row[storeId]) {
				row[storeId].qty = (Number(row[storeId].qty) || 0) + pax;
				updatedCount += 1;
			}
		});
	}));

	const updatedRows = [...rowsByVariant.values(), ...notFound.values()]
		.map((row, idx) => ({ ...row, 'S.No': idx + 1 }));
	const notes = [];
	if (unmappedStores.size) notes.push(`No store column for: ${[...unmappedStores].join(', ')}`);
	if (ignoredDecimals.length) notes.push(`Skipped pax that are not whole numbers: ${ignoredDecimals.join(', ')}`);
	return {
		updatedRows,
		matchedCount: rowsByVariant.size,
		updatedCount,
		addedCount: rowsByVariant.size,
		storeMapping,
		notes,
	};
};
